import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService, { Stats } from '../services/api';

const HomePage: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchSku, setSearchSku] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await apiService.getStats();
        setStats(data);
      } catch (err) {
        setError('Failed to load system statistics');
        console.error('Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchSku.trim()) {
      window.location.href = `/products/${searchSku.trim().toUpperCase()}`;
    }
  };

  return (
    <div className="container">
      <div className="row">
        <div className="col-12">
          <div className="jumbotron bg-light p-5 rounded-3 mb-4">
            <h1 className="display-4">Sistema de Histórico de Preços</h1>
            <p className="lead">
              Plataforma para visualização e análise do histórico de preços dos produtos
            </p>
            <hr className="my-4" />
            <p>
              Acesse o histórico completo de preços, compare diferentes tipos de preço 
              (ZMSR, ZPR1, ZPM3) e analise tendências ao longo do tempo.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Busca Rápida por SKU</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSearch}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Digite o SKU do produto (ex: P036895)"
                    value={searchSku}
                    onChange={(e) => setSearchSku(e.target.value)}
                  />
                  <button className="btn btn-primary" type="submit">
                    Buscar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* System Stats */}
      <div className="row mb-4">
        <div className="col-12">
          <h3>Estatísticas do Sistema</h3>
        </div>
      </div>

      {loading && (
        <div className="row">
          <div className="col-12">
            <div className="loading-spinner">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card stats-card text-white">
              <div className="card-body text-center">
                <h2 className="card-title">{stats.total_products.toLocaleString()}</h2>
                <p className="card-text">Produtos</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body text-center">
                <h2 className="card-title">{stats.total_records.toLocaleString()}</h2>
                <p className="card-text">Registros de Preço</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body text-center">
                <h6 className="card-title">Período</h6>
                <p className="card-text">
                  {stats.earliest_date ? new Date(stats.earliest_date).toLocaleDateString('pt-PT') : 'N/A'}
                </p>
                <small>até</small>
                <p className="card-text">
                  {stats.latest_date ? new Date(stats.latest_date).toLocaleDateString('pt-PT') : 'N/A'}
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-dark">
              <div className="card-body text-center">
                <h6 className="card-title">Tipos de Preço</h6>
                <p className="card-text">ZMSR, ZPR1, ZPM3</p>
                <small>Preço praticado = MIN(tipos)</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="row">
        <div className="col-12">
          <h3>Ações Rápidas</h3>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Explorar Produtos</h5>
            </div>
            <div className="card-body">
              <p className="card-text">
                Navegue pela lista completa de produtos e veja os preços atuais.
              </p>
              <Link to="/products" className="btn btn-primary">
                Ver Todos os Produtos
              </Link>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Exemplo de Produto</h5>
            </div>
            <div className="card-body">
              <p className="card-text">
                Veja um exemplo completo de histórico de preços.
              </p>
              <Link to="/products/P036895" className="btn btn-outline-primary">
                Ver Produto P036895
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;